import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import InstitutionReportView from "../components/InstitutionReportView";
import "../css/InstitutionDashboard.css";

const API = process.env.REACT_APP_API_BASE || "https://beintelli.engaggio.com/handlers";

const InstitutionDashboard = () => {
  const { fetchInstitutionDashboard, institutionData, loading, fetchInstitutionReportData } = useAuth();

  const [tab, setTab] = useState("overview");
  const [assignEducatorId, setAssignEducatorId] = useState("");
  const [assignLearnerId, setAssignLearnerId] = useState("");
  const [linkEducatorId, setLinkEducatorId] = useState("");
  const [assignMsg, setAssignMsg] = useState("");
  const [linkMsg, setLinkMsg] = useState("");

  // Create learner state
  const [newLearner, setNewLearner] = useState({ child_name: "", grade: "", section: "", dob: "" });
  const [createMsg, setCreateMsg] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");

  // Report state
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMsg, setReportMsg] = useState("");

  // Create educator state
  const [newEducator, setNewEducator] = useState({ name: "", email: "", phone: "", password: "" });
  const [createEducatorMsg, setCreateEducatorMsg] = useState("");

  useEffect(() => {
    fetchInstitutionDashboard();
  }, []);

  const token = localStorage.getItem("token");

  const handleAssignEducatorToLearner = async () => {
    if (!assignEducatorId || !assignLearnerId) return setAssignMsg("Select both educator and learner.");
    try {
      const res = await fetch(`${API}/user/institution/assign-educator-learner`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ educator_id: assignEducatorId, learner_id: assignLearnerId }),
      });
      const data = await res.json();
      setAssignMsg(res.ok ? "✅ " + data.message : "❌ " + data.message);
    } catch {
      setAssignMsg("❌ Request failed");
    }
  };

  const handleLinkEducator = async () => {
    if (!linkEducatorId) return setLinkMsg("Enter educator ID.");
    try {
      const res = await fetch(`${API}/user/institution/link-educator`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ educator_id: linkEducatorId }),
      });
      const data = await res.json();
      setLinkMsg(res.ok ? "✅ " + data.message : "❌ " + data.message);
    } catch {
      setLinkMsg("❌ Request failed");
    }
  };

  const handleCreateEducator = async () => {
    if (!newEducator.name || !newEducator.email || !newEducator.password)
      return setCreateEducatorMsg("Name, email, and password are required.");
    setCreateEducatorMsg("");
    try {
      const res = await fetch(`${API}/user/institution/create-educator`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newEducator),
      });
      const data = await res.json();
      if (res.ok) {
        setCreateEducatorMsg("✅ Educator created!");
        setNewEducator({ name: "", email: "", phone: "", password: "" });
        fetchInstitutionDashboard();
      } else {
        setCreateEducatorMsg("❌ " + data.message);
      }
    } catch {
      setCreateEducatorMsg("❌ Request failed");
    }
  };

  const handleCreateLearner = async () => {
    if (!newLearner.child_name) return setCreateMsg("Child name is required.");
    setCreateMsg("");
    setGeneratedCode("");
    try {
      const res = await fetch(`${API}/user/institution/create-learner`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newLearner),
      });
      const data = await res.json();
      if (res.ok) {
        setGeneratedCode(data.learner_code);
        setCreateMsg("✅ Learner created!");
        setNewLearner({ child_name: "", grade: "", section: "", dob: "" });
        fetchInstitutionDashboard();
      } else {
        setCreateMsg("❌ " + data.message);
      }
    } catch {
      setCreateMsg("❌ Request failed");
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="institution-dashboard">
      {/* ================= HEADER ================= */}
      <header className="dashboard-header">
        <h1>Institution Dashboard</h1>
        <p>{institutionData?.institution_name}</p>
        {institutionData?.institution_code && (
          <p style={{ fontSize: 13, color: "#64748b" }}>
            Educator Join Code: <strong style={{ letterSpacing: 2, color: "#1e293b" }}>{institutionData.institution_code}</strong>
          </p>
        )}
      </header>

      {/* ================= STATS ================= */}
      <section className="stats-grid">
        <div className="stat-card">
          <h3>Total Learners</h3>
          <p>{institutionData?.stats?.learners || 0}</p>
        </div>

        <div className="stat-card">
          <h3>Total Educators</h3>
          <p>{institutionData?.stats?.educators || 0}</p>
        </div>

        <div className="stat-card">
          <h3>Assessments This Month</h3>
          <p>{institutionData?.stats?.assessments || 0}</p>
        </div>

        <div className="stat-card">
          <h3>Avg Engagement Score</h3>
          <p>{institutionData?.stats?.avg_score || 0}</p>
        </div>
      </section>

      {/* ================= ALERTS ================= */}
      {institutionData?.alerts?.length > 0 && (
        <section className="card highlight">
          <h2>Needs Attention</h2>
          <ul>
            {institutionData.alerts.map((l) => (
              <li key={l.learner_id}>
                {l.full_name} – Low Engagement
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ================= TABS ================= */}
      <div className="tabs">
        <button
          className={tab === "overview" ? "active" : ""}
          onClick={() => setTab("overview")}
        >
          Overview
        </button>

        <button
          className={tab === "educators" ? "active" : ""}
          onClick={() => setTab("educators")}
        >
          Educators
        </button>

        <button
          className={tab === "learners" ? "active" : ""}
          onClick={() => setTab("learners")}
        >
          Learners
        </button>

        <button
          className={tab === "assign" ? "active" : ""}
          onClick={() => setTab("assign")}
        >
          Assign
        </button>

        <button
          className={tab === "create" ? "active" : ""}
          onClick={() => setTab("create")}
        >
          + Add Learner
        </button>

        <button
          className={tab === "create-educator" ? "active" : ""}
          onClick={() => setTab("create-educator")}
        >
          + Add Educator
        </button>

        <button
          className={tab === "report" ? "active" : ""}
          onClick={() => setTab("report")}
        >
          Institution Report
        </button>
      </div>

      {/* ================= OVERVIEW ================= */}
      {tab === "overview" && (
        <section className="card">
          <h2>Upcoming Assessments</h2>

          {institutionData?.upcoming?.length ? (
            <ul>
              {institutionData.upcoming.map((l) => (
                <li key={l.learner_id}>
                  {l.full_name} –{" "}
                  {new Date(l.next_assessment_date).toDateString()}
                </li>
              ))}
            </ul>
          ) : (
            <p>No upcoming assessments</p>
          )}
        </section>
      )}

      {/* ================= EDUCATORS ================= */}
      {tab === "educators" && (
        <section className="card">
          <h2>Educators</h2>

          {institutionData?.educators?.length ? (
            <ul>
              {institutionData.educators.map((edu) => (
                <li key={edu.id}>
                  {edu.full_name} – {edu.subject}
                </li>
              ))}
            </ul>
          ) : (
            <p>No educators found</p>
          )}
        </section>
      )}

      {/* ================= LEARNERS ================= */}
      {tab === "learners" && (
        <section className="card">
          <h2>Learners</h2>

          {institutionData?.learners?.length ? (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Grade</th>
                  <th style={s.th}>Last Assessment</th>
                  <th style={s.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {institutionData.learners.map((learner) => (
                  <tr key={learner.id} style={s.tr}>
                    <td style={s.td}><strong>{learner.full_name}</strong></td>
                    <td style={s.td}>{learner.grade || "—"}</td>
                    <td style={s.td}>
                      {learner.last_assessment
                        ? new Date(learner.last_assessment).toDateString()
                        : "No assessment"}
                    </td>
                    <td style={s.td}>
                      {learner.overall_status
                        ? <span style={{...s.badge, ...statusColor(learner.overall_status)}}>{learner.overall_status}</span>
                        : <span style={s.noData}>No data</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No learners found</p>
          )}
        </section>
      )}

      {/* ================= ASSIGN ================= */}
      {tab === "assign" && (
        <>
          <section className="card">
            <h2>Assign Educator to Learner</h2>
            <p style={s.hint}>Select an educator and a learner from this institution to link them.</p>

            <div style={s.row}>
              <select style={s.select} value={assignEducatorId} onChange={(e) => setAssignEducatorId(e.target.value)}>
                <option value="">— Select Educator —</option>
                {(institutionData?.educators || []).map((edu) => (
                  <option key={edu.id} value={edu.id}>{edu.username || edu.full_name} ({edu.email})</option>
                ))}
              </select>

              <select style={s.select} value={assignLearnerId} onChange={(e) => setAssignLearnerId(e.target.value)}>
                <option value="">— Select Learner —</option>
                {(institutionData?.learners || []).map((l) => (
                  <option key={l.id} value={l.id}>{l.full_name} (Grade {l.grade || "?"})</option>
                ))}
              </select>

              <button style={s.btn} onClick={handleAssignEducatorToLearner}>Assign</button>
            </div>
            {assignMsg && <p style={s.msg}>{assignMsg}</p>}
          </section>

          <section className="card">
            <h2>Link Existing Educator by ID</h2>
            <p style={s.hint}>If an educator registered independently, enter their user ID to link them to this institution.</p>

            <div style={s.row}>
              <input
                style={s.input}
                type="number"
                placeholder="Educator user ID"
                value={linkEducatorId}
                onChange={(e) => setLinkEducatorId(e.target.value)}
              />
              <button style={s.btn} onClick={handleLinkEducator}>Link</button>
            </div>
            {linkMsg && <p style={s.msg}>{linkMsg}</p>}
          </section>
        </>
      )}

      {/* ================= CREATE EDUCATOR ================= */}
      {tab === "create-educator" && (
        <section className="card">
          <h2>Add New Educator</h2>
          <p style={s.hint}>Create an educator account for this institution.</p>

          <div style={s.formGrid}>
            <input
              style={s.input}
              placeholder="Full Name *"
              value={newEducator.name}
              onChange={(e) => setNewEducator({ ...newEducator, name: e.target.value })}
            />
            <input
              style={s.input}
              type="email"
              placeholder="Email *"
              value={newEducator.email}
              onChange={(e) => setNewEducator({ ...newEducator, email: e.target.value })}
            />
            <input
              style={s.input}
              placeholder="Phone (optional)"
              value={newEducator.phone}
              onChange={(e) => setNewEducator({ ...newEducator, phone: e.target.value })}
            />
            <input
              style={s.input}
              type="password"
              placeholder="Password *"
              value={newEducator.password}
              onChange={(e) => setNewEducator({ ...newEducator, password: e.target.value })}
            />
          </div>

          <button style={{ ...s.btn, marginTop: 16 }} onClick={handleCreateEducator}>
            Create Educator
          </button>

          {createEducatorMsg && <p style={s.msg}>{createEducatorMsg}</p>}
        </section>
      )}

      {/* ================= CREATE LEARNER ================= */}
      {tab === "create" && (
        <section className="card">
          <h2>Add New Learner</h2>
          <p style={s.hint}>Create a student account. A parent code will be generated — share it with the parent to link their child.</p>

          <div style={s.formGrid}>
            <input
              style={s.input}
              placeholder="Full Name *"
              value={newLearner.child_name}
              onChange={(e) => setNewLearner({ ...newLearner, child_name: e.target.value })}
            />
            <input
              style={s.input}
              placeholder="Grade (e.g. 7)"
              value={newLearner.grade}
              onChange={(e) => setNewLearner({ ...newLearner, grade: e.target.value })}
            />
            <input
              style={s.input}
              placeholder="Section (e.g. A)"
              value={newLearner.section}
              onChange={(e) => setNewLearner({ ...newLearner, section: e.target.value })}
            />
            <input
              style={s.input}
              type="date"
              placeholder="Date of Birth"
              value={newLearner.dob}
              onChange={(e) => setNewLearner({ ...newLearner, dob: e.target.value })}
            />
          </div>

          <button style={{ ...s.btn, marginTop: 16 }} onClick={handleCreateLearner}>
            Create Learner
          </button>

          {createMsg && <p style={s.msg}>{createMsg}</p>}

          {generatedCode && (
            <div style={s.codeBox}>
              <p style={s.codeLabel}>Parent Code — share this with the parent:</p>
              <p style={s.codeValue}>{generatedCode}</p>
            </div>
          )}
        </section>
      )}

      {/* ================= INSTITUTION REPORT ================= */}
      {tab === "report" && (
        <section className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>Institution Report</h2>
            {!reportData && (
              <button
                style={s.btn}
                onClick={async () => {
                  setReportLoading(true);
                  setReportMsg("");
                  const data = await fetchInstitutionReportData();
                  if (data) { setReportData(data); }
                  else { setReportMsg("❌ Could not generate report. Make sure learners have completed assessments."); }
                  setReportLoading(false);
                }}
                disabled={reportLoading}
              >
                {reportLoading ? "Generating…" : "Generate Report"}
              </button>
            )}
            {reportData && (
              <button style={{ ...s.btn, background: "#64748b" }} onClick={() => setReportData(null)}>
                Regenerate
              </button>
            )}
          </div>
          {reportMsg && <p style={{ fontSize: 13, color: "#dc2626" }}>{reportMsg}</p>}
          {reportLoading && <p style={s.hint}>Generating AI institution report… this may take a moment.</p>}
          {reportData && (
            <InstitutionReportView
              data={reportData}
              onDownload={() => {
                if (reportData?.file_path) {
                  window.open(`${API.replace("/handlers", "")}${reportData.file_path}`, "_blank");
                }
              }}
              downloadLoading={false}
            />
          )}
          {!reportData && !reportLoading && !reportMsg && (
            <p style={s.hint}>Click "Generate Report" to create a Gemini AI institution engagement report for all your learners.</p>
          )}
        </section>
      )}
    </div>
  );
};

const statusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "good":        return { background: "#dcfce7", color: "#166534" };
    case "very good":   return { background: "#dcfce7", color: "#166534" };
    case "average":     return { background: "#fef9c3", color: "#854d0e" };
    case "low":         return { background: "#fee2e2", color: "#991b1b" };
    case "support":     return { background: "#fee2e2", color: "#991b1b" };
    default:            return { background: "#f1f5f9", color: "#64748b" };
  }
};

const s = {
  table:  { width: "100%", borderCollapse: "collapse" },
  th:     { textAlign: "left", padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#64748b", borderBottom: "1px solid #e2e8f0", textTransform: "uppercase" },
  tr:     { borderBottom: "1px solid #f1f5f9" },
  td:     { padding: "12px 12px", fontSize: 14, color: "#1e293b" },
  badge:  { display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  noData: { fontSize: 12, color: "#94a3b8" },
  hint:      { fontSize: 13, color: "#64748b", margin: "0 0 14px" },
  row:       { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  select:    { flex: 1, minWidth: 180, padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14 },
  input:     { flex: 1, minWidth: 180, padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14 },
  btn:       { padding: "9px 20px", background: "#1e293b", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  msg:       { marginTop: 10, fontSize: 13 },
  formGrid:  { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 },
  codeBox:   { marginTop: 20, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "16px 20px", textAlign: "center" },
  codeLabel: { fontSize: 13, color: "#166534", margin: "0 0 8px" },
  codeValue: { fontSize: 28, fontWeight: 800, letterSpacing: 4, color: "#15803d", margin: 0 },
};

export default InstitutionDashboard;